/**
 * PerformanceMonitor Component
 * 
 * Monitors Core Web Vitals and reports to analytics
 * Helps track real-world performance metrics
 */
import { useEffect } from 'react';

const PerformanceMonitor = () => {
  useEffect(() => {
    // Only run in production and if browser supports Performance API
    if (process.env.NODE_ENV !== 'production' || !('PerformanceObserver' in window)) {
      return;
    }

    // Monitor Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      if (lastEntry && window.posthog) {
        window.posthog.capture('web_vitals', {
          metric: 'LCP',
          value: Math.round(lastEntry.renderTime || lastEntry.loadTime),
          rating: lastEntry.renderTime < 2500 ? 'good' : lastEntry.renderTime < 4000 ? 'needs-improvement' : 'poor'
        });
      }
    });

    try {
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      // LCP not supported
    }

    // Monitor First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (window.posthog) {
          window.posthog.capture('web_vitals', {
            metric: 'FID',
            value: Math.round(entry.processingStart - entry.startTime),
            rating: entry.processingStart - entry.startTime < 100 ? 'good' : entry.processingStart - entry.startTime < 300 ? 'needs-improvement' : 'poor'
          });
        }
      });
    });

    try {
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      // FID not supported
    }

    // Monitor Cumulative Layout Shift (CLS)
    let clsValue = 0;
    let clsEntries = [];

    const clsObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      
      entries.forEach((entry) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
          clsEntries.push(entry);
        }
      });

      // Report CLS when page is hidden or unloaded
      if (window.posthog && clsEntries.length > 0) {
        window.posthog.capture('web_vitals', {
          metric: 'CLS',
          value: Math.round(clsValue * 1000) / 1000,
          rating: clsValue < 0.1 ? 'good' : clsValue < 0.25 ? 'needs-improvement' : 'poor'
        });
      }
    });

    try {
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (e) {
      // CLS not supported
    }

    // Report navigation timing
    const reportNavigationTiming = () => {
      if (!window.performance || !window.performance.getEntriesByType) return;

      const navigation = window.performance.getEntriesByType('navigation')[0];
      if (!navigation) return;

      const metrics = {
        dns: Math.round(navigation.domainLookupEnd - navigation.domainLookupStart),
        tcp: Math.round(navigation.connectEnd - navigation.connectStart),
        request: Math.round(navigation.responseStart - navigation.requestStart),
        response: Math.round(navigation.responseEnd - navigation.responseStart),
        dom: Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart),
        load: Math.round(navigation.loadEventEnd - navigation.loadEventStart),
        total: Math.round(navigation.loadEventEnd - navigation.fetchStart),
      };

      if (window.posthog) {
        window.posthog.capture('navigation_timing', metrics);
      }
    };

    // Report after page load
    if (document.readyState === 'complete') {
      setTimeout(reportNavigationTiming, 0);
    } else {
      window.addEventListener('load', reportNavigationTiming);
    }

    // Cleanup
    return () => {
      lcpObserver.disconnect();
      fidObserver.disconnect();
      clsObserver.disconnect();
    };
  }, []);

  return null; // This component doesn't render anything
};

export default PerformanceMonitor;
